import React, { useState, useEffect, useRef } from 'react';
import { Upload, Save, RefreshCw, AlertTriangle, X, CheckCircle, FileDown, Printer, Search, ChevronDown } from 'lucide-react';
import * as XLSX from 'xlsx';
import {
  Box,
  CircularProgress,
  TextField,
  Button,
  FormControl,
  Select,
  MenuItem
} from '@mui/material';
import MainService from '../../services/MainService';
import { getApiBaseUrl } from '../../services/appSettings';
import TypeaheadInput from '../ui/TypeaheadInput';

const ClientAutocomplete = ({
  value,
  onChange,
  placeholder = 'Search by name or mobile...',
  disabled = false,
  label,
}) => (
  <TypeaheadInput
    label={label}
    value={value}
    onChange={onChange}
    disabled={disabled}
    placeholder={placeholder}
    emptyText="No clients found"
    getDisplayValue={(item) => item?.Name || item?.MobileNo1 || ''}
    renderOption={(client) => (
      <>
        <div className="font-medium">{client.Name}</div>
        <div className="text-xs text-gray-500">{client.MobileNo1}</div>
      </>
    )}
    fetchOptions={async (keyword) => MainService.ClientsInOrderAutoComplete({ Keyword: keyword })}
  />
);

const OrderUpload = () => {
  const [orders, setOrders] = useState([]);
  const [emirates, setEmirates] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filter, setFilter] = useState({
    PickupDate: new Date().toISOString().split('T')[0],
    DeliveryDate: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  });

  const fileInputRef = useRef(null);

  useEffect(() => {
    loadEmirates();
    loadCompanyProfile();
  }, []);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (selectedClient?.Id) {
      loadClientAddresses();
    }
  }, [selectedClient]);

  const loadEmirates = async () => {
    try {
      const response = await MainService.EmiratesInDropDown();
      setEmirates(response || []);
    } catch (err) {
      console.error('Error loading emirates:', err);
    }
  };

  const loadCompanyProfile = async () => {
    try {
      const response = await MainService.GetCompanyProfile();
      setCompanyProfile(response);
      if (response && !response.IsSameDay) {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        setFilter(prev => ({
          ...prev,
          DeliveryDate: tomorrow.toISOString().split('T')[0]
        }));
      }
    } catch (err) {
      console.error('Error loading company profile:', err);
    }
  };

  const loadClientAddresses = async () => {
    if (!selectedClient?.Id) return;
    try {
      const response = await MainService.GetClientAddress(selectedClient.Id);
      setAddresses(response || []);
    } catch (err) {
      console.error('Error loading addresses:', err);
      setError('Failed to load client addresses');
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = e.target.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        const processedOrders = jsonData.map((row) => {
          const order = {
            VoucherNo: row.VoucherNo || '',
            ClientBillNo: row.ClientBillNo || '',
            ChargeFrom: companyProfile?.DefChargeFrom || 'CLIENT',
            PayType: row.PayType || 'COD',
            CustomerName: row.Customer || row.CustomerName || '',
            MobileNo: row.MobileNo || '',
            MobileNo2: row.MobileNo2 || '',
            Emirate: row.Emirate || '',
            EmirateId: '',
            Location: row.Location || '',
            LocationId: '',
            Locations: [],
            Address: row.Address || '',
            PickupDate: filter.PickupDate,
            DeliveryDate: filter.DeliveryDate,
            Quantity: parseFloat(row.Quantity) || 0,
            ProductPrice: parseFloat(row.ProductPrice) || 0,
            DeliveryCharge: 0,
            TAX: 0,
            CustomerAmount: 0,
            ClientAmount: 0,
            Remarks: row.DeliveryInstructions || row.Remarks || '',
            IsPickup: row.IsPickup == 1,
            IsExchange: row.IsExchange == 1,
            IsFree: row.IsFree == 1,
            IsAdvancePay: false,
            IsAdvancePrice: false,
            TempServiceCharge: 0
          };

          checkEmirate(order);
          return order;
        });

        setOrders(processedOrders);
        setSuccess(`Loaded ${processedOrders.length} orders from Excel`);
      } catch (err) {
        console.error('Error parsing Excel:', err);
        setError('Failed to parse Excel file. Please check the format.');
      }
    };

    reader.readAsBinaryString(file);
  };

  const checkEmirate = async (order) => {
    if (!order.Emirate) return;
    
    let bestMatch = null;
    let bestSimilarity = 0;

    emirates.forEach((emirate) => {
      const similarity = calculateSimilarity(emirate.Name.toLowerCase(), order.Emirate.toLowerCase());
      if (similarity > bestSimilarity) {
        bestSimilarity = similarity;
        bestMatch = emirate;
      }
    });

    if (bestMatch) {
      order.EmirateId = bestMatch.Id;
      await loadLocationsForOrder(order);
    }
  };

  const calculateSimilarity = (s1, s2) => {
    const longer = s1.length > s2.length ? s1 : s2;
    const shorter = s1.length > s2.length ? s2 : s1;
    const longerLength = longer.length;
    
    if (longerLength === 0) return 1.0;
    
    const editDistance = getEditDistance(longer, shorter);
    return (longerLength - editDistance) / longerLength;
  };

  const getEditDistance = (s1, s2) => {
    const costs = [];
    for (let i = 0; i <= s1.length; i++) {
      let lastValue = i;
      for (let j = 0; j <= s2.length; j++) {
        if (i === 0) {
          costs[j] = j;
        } else if (j > 0) {
          let newValue = costs[j - 1];
          if (s1.charAt(i - 1) !== s2.charAt(j - 1)) {
            newValue = Math.min(Math.min(newValue, lastValue), costs[j]) + 1;
          }
          costs[j - 1] = lastValue;
          lastValue = newValue;
        }
      }
      if (i > 0) costs[s2.length] = lastValue;
    }
    return costs[s2.length];
  };

  const loadLocationsForOrder = async (order) => {
    if (!order.EmirateId) return;
    try {
      const response = await MainService.LocationByEmirateId(order.EmirateId);
      order.Locations = response || [];
    } catch (err) {
      console.error('Error loading locations:', err);
    }
  };

  const handleOrderFieldChange = (index, field, value) => {
    setOrders(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        [field]: value
      };
      
      if (field === 'EmirateId') {
        updated[index].LocationId = '';
        loadLocationsForOrder(updated[index]);
        if (updated[index].Locations.length > 0) {
          updated[index].LocationId = updated[index].Locations[0].Id;
        }
        checkDeliveryCharges(updated[index]);
      } else if (field === 'LocationId') {
        checkDeliveryCharges(updated[index]);
      } else if (field === 'ChargeFrom') {
        checkDeliveryCharges(updated[index]);
      } else if (field === 'PayType' || field === 'ProductPrice' || field === 'DeliveryCharge' || 
                 field === 'IsAdvancePay' || field === 'IsAdvancePrice') {
        calculateTotals(updated[index]);
      } else if (field === 'IsFree') {
        checkDeliveryCharges(updated[index]);
      }
      
      return updated;
    });
  };

  const checkDeliveryCharges = async (order) => {
    if (order.IsFree) {
      order.DeliveryCharge = 0;
      order.TempServiceCharge = 0;
      calculateTotals(order);
      return;
    }

    if (!order.LocationId || !selectedClient?.Id) {
      order.DeliveryCharge = companyProfile?.DefDelCharge || 0;
      order.TempServiceCharge = order.DeliveryCharge;
      calculateTotals(order);
      return;
    }

    try {
      const response = await MainService.GetDeliveryCharge({
        ChargeFrom: order.ChargeFrom,
        LocationId: order.LocationId,
        ClientId: selectedClient.Id
      });

      if (response > 0) {
        order.DeliveryCharge = response;
      } else {
        order.DeliveryCharge = companyProfile?.DefDelCharge || 0;
      }
      order.TempServiceCharge = order.DeliveryCharge;
      calculateTotals(order);
    } catch (err) {
      console.error('Error getting delivery charge:', err);
      order.DeliveryCharge = companyProfile?.DefDelCharge || 0;
      order.TempServiceCharge = order.DeliveryCharge;
      calculateTotals(order);
    }
  };

  const calculateTotals = (order) => {
    order.DeliveryCharge = order.TempServiceCharge || 0;

    if (companyProfile?.DefTAX > 0) {
      if (companyProfile.IsVATFromDelivery) {
        order.TAX = order.DeliveryCharge * (companyProfile.DefTAX / 100);
        order.TAX = Math.round(order.TAX * 100) / 100;
        order.DeliveryCharge = order.DeliveryCharge - order.TAX;
      } else {
        order.TAX = order.DeliveryCharge * (companyProfile.DefTAX / 100);
        order.TAX = Math.round(order.TAX * 100) / 100;
      }
    } else {
      order.TAX = 0;
    }

    if (order.PayType === 'CAD') {
      order.CustomerAmount = order.ProductPrice;
      if (order.IsAdvancePrice) {
        order.ClientAmount = 0 - (order.DeliveryCharge + order.TAX);
      } else {
        if (order.IsAdvancePay) {
          order.ClientAmount = order.ProductPrice;
        } else {
          order.ClientAmount = order.ProductPrice - (order.DeliveryCharge + order.TAX);
        }
      }
    } else {
      order.CustomerAmount = order.ProductPrice + order.DeliveryCharge + order.TAX;
      if (order.IsAdvancePrice) {
        order.ClientAmount = 0;
      } else {
        if (order.IsAdvancePay) {
          order.ClientAmount = order.ProductPrice;
        } else {
          order.ClientAmount = order.ProductPrice;
        }
      }
    }

    setOrders(prev => [...prev]);
  };

  const applyDate = () => {
    setOrders(prev => prev.map(order => ({
      ...order,
      PickupDate: filter.PickupDate,
      DeliveryDate: filter.DeliveryDate
    })));
  };

  const applyLocation = () => {
    setOrders(prev => prev.map(order => {
      if (order.Locations && order.Locations.length > 0) {
        return {
          ...order,
          LocationId: order.Locations[0].Id
        };
      }
      return order;
    }));
  };

  const applyExactLocation = async () => {
    for (const order of orders) {
      if (order.EmirateId && order.Location) {
        try {
          const response = await MainService.getLocationByNameEmiId({
            EmirateId: order.EmirateId,
            Name: order.Location
          });
          if (response) {
            order.LocationId = response.Id;
            checkDeliveryCharges(order);
          }
        } catch (err) {
          console.error('Error getting exact location:', err);
        }
      }
    }
    setOrders([...orders]);
  };

  const applyAllPickup = () => {
    setOrders(prev => prev.map(order => ({
      ...order,
      IsPickup: true
    })));
  };

  const handleInit = () => {
    setOrders([]);
    setSelectedClient(null);
    setAddresses([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUploadOrders = async (shouldPrint = false) => {
    // Validation
    const invalidOrders = orders.filter(order => 
      !order.EmirateId || !order.LocationId || !order.CustomerName || 
      !order.MobileNo || (order.PayType !== 'COD' && order.PayType !== 'CAD')
    );

    if (invalidOrders.length > 0) {
      setError('Invalid Format: CAD/COD, Emirate, Location, Customer, Customer Mobile No needed');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const ordersToSave = orders.map(order => {
        const pickupDate = new Date(order.PickupDate);
        const deliveryDate = new Date(order.DeliveryDate);

        return {
          ...order,
          Customer: {
            MobileNo1: order.MobileNo,
            MobileNo2: order.MobileNo2,
            Name: order.CustomerName
          },
          CustomerAddress: {
            LocationId: order.LocationId,
            EmirateId: order.EmirateId,
            Address: order.Address
          },
          PMonth: pickupDate.getMonth() + 1,
          PDay: pickupDate.getDate(),
          PYear: pickupDate.getFullYear(),
          DMonth: deliveryDate.getMonth() + 1,
          DDay: deliveryDate.getDate(),
          DYear: deliveryDate.getFullYear(),
          ClientId: selectedClient.Id,
          Client: selectedClient,
          ClientAddressId: selectedClient.AddressId
        };
      });

      const response = await MainService.SaveOrderList(ordersToSave);

      if (response?.Status === 'Success') {
        setSuccess('Orders uploaded successfully');
        if (shouldPrint && response.OrderIds) {
          handlePrintOrders(response.OrderIds);
        }
        handleInit();
      } else {
        setError(response?.Status || 'Failed to upload orders');
      }
    } catch (err) {
      console.error('Error uploading orders:', err);
      setError('Failed to upload orders');
    } finally {
      setLoading(false);
    }
  };

  const handlePrintOrders = async (orderIds) => {
    try {
      const response = await MainService.PrintOrderList(orderIds);
      if (response) {
        window.open(`${getApiBaseUrl()}ReportFiles/${response}`, '_blank');
        setSuccess('Printing orders...');
      } else {
        setError('Print error');
      }
    } catch (err) {
      console.error('Error printing orders:', err);
      setError('Print error');
    }
  };

  const downloadTemplate = () => {
    const link = document.createElement('a');
    link.href = `${process.env.PUBLIC_URL || ''}/excel/BulkOrder.xlsx`;
    link.download = 'BulkOrder.xlsx';
    link.click();
  };

  return (
    <div className="space-y-6">
      {/* Toast Messages */}
      {error && (
        <div className="fixed top-4 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="fixed top-4 right-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{success}</span>
          <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Order Upload</h1>
            <p className="text-sm text-gray-500 mt-1">Upload orders from Excel file</p>
          </div>
          <Button
            variant="outlined"
            startIcon={<FileDown className="w-4 h-4" />}
            onClick={downloadTemplate}
            sx={{
              borderColor: '#46955A',
              color: '#46955A',
              '&:hover': { borderColor: '#3d7d4f', backgroundColor: '#f0f9f4' }
            }}
          >
            Download Excel Template
          </Button>
        </div>

        {/* Client Selection */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div>
            <ClientAutocomplete
              value={selectedClient}
              onChange={setSelectedClient}
              label="Client Name"
              placeholder="Search by name..."
            />
          </div>
          <div>
            <ClientAutocomplete
              value={selectedClient}
              onChange={setSelectedClient}
              label="Client Mobile No"
              placeholder="Search by mobile..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Select Address</label>
            <FormControl fullWidth size="small">
              <Select
                value={selectedClient?.AddressId || ''}
                onChange={(e) => {
                  if (selectedClient) {
                    setSelectedClient({ ...selectedClient, AddressId: e.target.value });
                  }
                }}
                disabled={!selectedClient}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: '#46955A',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#46955A',
                    },
                  },
                }}
              >
                {addresses.map((addr) => (
                  <MenuItem key={addr.Id} value={addr.Id}>
                    {addr.Address} - {addr.Location?.Name} - {addr.Emirate?.Name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Choose Excel File</label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileUpload}
              disabled={!selectedClient}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#46955A] text-sm"
            />
          </div>
        </div>

        {/* Date Filters and Actions */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Pickup Date</label>
            <TextField
              type="date"
              value={filter.PickupDate}
              onChange={(e) => setFilter(prev => ({ ...prev, PickupDate: e.target.value }))}
              size="small"
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: '#46955A',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#46955A',
                  },
                },
              }}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Delivery Date</label>
            <TextField
              type="date"
              value={filter.DeliveryDate}
              onChange={(e) => setFilter(prev => ({ ...prev, DeliveryDate: e.target.value }))}
              size="small"
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: '#46955A',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#46955A',
                  },
                },
              }}
            />
          </div>
          <div className="flex items-end">
            <Button
              variant="outlined"
              onClick={applyDate}
              disabled={orders.length === 0}
              fullWidth
              sx={{
                borderColor: '#46955A',
                color: '#46955A',
                '&:hover': { borderColor: '#3d7d4f', backgroundColor: '#f0f9f4' }
              }}
            >
              Apply Date
            </Button>
          </div>
          <div className="flex items-end">
            <Button
              variant="outlined"
              onClick={applyLocation}
              disabled={orders.length === 0}
              fullWidth
              sx={{
                borderColor: '#46955A',
                color: '#46955A',
                '&:hover': { borderColor: '#3d7d4f', backgroundColor: '#f0f9f4' }
              }}
            >
              Apply Any Location
            </Button>
          </div>
          <div className="flex items-end">
            <Button
              variant="outlined"
              onClick={applyExactLocation}
              disabled={orders.length === 0}
              fullWidth
              sx={{
                borderColor: '#46955A',
                color: '#46955A',
                '&:hover': { borderColor: '#3d7d4f', backgroundColor: '#f0f9f4' }
              }}
            >
              Apply Exact Location
            </Button>
          </div>
          <div className="flex items-end">
            <Button
              variant="outlined"
              onClick={applyAllPickup}
              disabled={orders.length === 0}
              fullWidth
              sx={{
                borderColor: '#46955A',
                color: '#46955A',
                '&:hover': { borderColor: '#3d7d4f', backgroundColor: '#f0f9f4' }
              }}
            >
              Apply All Pickup
            </Button>
          </div>
        </div>
      </div>

      {/* Orders Table - Excel Style */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-auto" style={{ maxHeight: '600px' }}>
          <table className="w-full border-collapse" style={{ minWidth: '1400px' }}>
            <thead className="sticky top-0 z-10">
              <tr className="bg-gray-50">
                <th className="border border-gray-300 px-2 py-2 text-left text-xs font-semibold text-gray-700 bg-gray-100" style={{ minWidth: '40px' }}>#</th>
                <th className="border border-gray-300 px-2 py-2 text-left text-xs font-semibold text-gray-700 bg-gray-100" style={{ minWidth: '120px' }}>Voucher No</th>
                <th className="border border-gray-300 px-2 py-2 text-left text-xs font-semibold text-gray-700 bg-gray-100" style={{ minWidth: '120px' }}>Client Bill No</th>
                <th className="border border-gray-300 px-2 py-2 text-left text-xs font-semibold text-gray-700 bg-gray-100" style={{ minWidth: '100px' }}>Charge From</th>
                <th className="border border-gray-300 px-2 py-2 text-left text-xs font-semibold text-gray-700 bg-gray-100" style={{ minWidth: '100px' }}>Pay Type</th>
                <th className="border border-gray-300 px-2 py-2 text-left text-xs font-semibold text-gray-700 bg-gray-100" style={{ minWidth: '150px' }}>Customer</th>
                <th className="border border-gray-300 px-2 py-2 text-left text-xs font-semibold text-gray-700 bg-gray-100" style={{ minWidth: '120px' }}>Mobile No</th>
                <th className="border border-gray-300 px-2 py-2 text-left text-xs font-semibold text-gray-700 bg-gray-100" style={{ minWidth: '120px' }}>Mobile No2</th>
                <th className="border border-gray-300 px-2 py-2 text-left text-xs font-semibold text-gray-700 bg-gray-100" style={{ minWidth: '120px' }}>Emirate</th>
                <th className="border border-gray-300 px-2 py-2 text-left text-xs font-semibold text-gray-700 bg-gray-100" style={{ minWidth: '150px' }}>Location</th>
                <th className="border border-gray-300 px-2 py-2 text-left text-xs font-semibold text-gray-700 bg-gray-100" style={{ minWidth: '200px' }}>Address</th>
                <th className="border border-gray-300 px-2 py-2 text-left text-xs font-semibold text-gray-700 bg-gray-100" style={{ minWidth: '100px' }}>Pickup Date</th>
                <th className="border border-gray-300 px-2 py-2 text-left text-xs font-semibold text-gray-700 bg-gray-100" style={{ minWidth: '100px' }}>Delivery Date</th>
                <th className="border border-gray-300 px-2 py-2 text-left text-xs font-semibold text-gray-700 bg-gray-100" style={{ minWidth: '80px' }}>Quantity</th>
                <th className="border border-gray-300 px-2 py-2 text-left text-xs font-semibold text-gray-700 bg-gray-100" style={{ minWidth: '120px' }}>Product Price</th>
                <th className="border border-gray-300 px-2 py-2 text-left text-xs font-semibold text-gray-700 bg-gray-100" style={{ minWidth: '120px' }}>Delivery Charge</th>
                <th className="border border-gray-300 px-2 py-2 text-left text-xs font-semibold text-gray-700 bg-gray-100" style={{ minWidth: '80px' }}>VAT</th>
                <th className="border border-gray-300 px-2 py-2 text-left text-xs font-semibold text-gray-700 bg-gray-100" style={{ minWidth: '120px' }}>Customer Amount</th>
                <th className="border border-gray-300 px-2 py-2 text-left text-xs font-semibold text-gray-700 bg-gray-100" style={{ minWidth: '120px' }}>Client Amount</th>
                <th className="border border-gray-300 px-2 py-2 text-left text-xs font-semibold text-gray-700 bg-gray-100" style={{ minWidth: '150px' }}>Remarks</th>
                <th className="border border-gray-300 px-2 py-2 text-left text-xs font-semibold text-gray-700 bg-gray-100" style={{ minWidth: '80px' }}>Pickup</th>
                <th className="border border-gray-300 px-2 py-2 text-left text-xs font-semibold text-gray-700 bg-gray-100" style={{ minWidth: '80px' }}>Advance Pay</th>
                <th className="border border-gray-300 px-2 py-2 text-left text-xs font-semibold text-gray-700 bg-gray-100" style={{ minWidth: '80px' }}>Exchange</th>
                <th className="border border-gray-300 px-2 py-2 text-left text-xs font-semibold text-gray-700 bg-gray-100" style={{ minWidth: '80px' }}>Free</th>
                <th className="border border-gray-300 px-2 py-2 text-left text-xs font-semibold text-gray-700 bg-gray-100" style={{ minWidth: '80px' }}>Advance Price</th>
              </tr>
            </thead>
            <tbody>
              {orders.length > 0 ? (
                orders.map((order, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="border border-gray-300 px-2 py-1 text-sm text-center bg-gray-50">{index + 1}</td>
                    <td className="border border-gray-300 px-1 py-0">
                      <input
                        type="text"
                        value={order.VoucherNo || ''}
                        onChange={(e) => handleOrderFieldChange(index, 'VoucherNo', e.target.value)}
                        className="w-full px-2 py-1 text-sm border-0 outline-none focus:bg-blue-50"
                        style={{ minWidth: '120px' }}
                      />
                    </td>
                    <td className="border border-gray-300 px-1 py-0">
                      <input
                        type="text"
                        value={order.ClientBillNo || ''}
                        onChange={(e) => handleOrderFieldChange(index, 'ClientBillNo', e.target.value)}
                        className="w-full px-2 py-1 text-sm border-0 outline-none focus:bg-blue-50"
                        style={{ minWidth: '120px' }}
                      />
                    </td>
                    <td className="border border-gray-300 px-1 py-0">
                      <select
                        value={order.ChargeFrom}
                        onChange={(e) => handleOrderFieldChange(index, 'ChargeFrom', e.target.value)}
                        className="w-full px-2 py-1 text-sm border-0 outline-none focus:bg-blue-50 bg-transparent"
                        style={{ minWidth: '100px' }}
                      >
                        <option value="CLIENT">Client</option>
                        <option value="AREA">Area</option>
                      </select>
                    </td>
                    <td className="border border-gray-300 px-1 py-0">
                      <select
                        value={order.PayType}
                        onChange={(e) => handleOrderFieldChange(index, 'PayType', e.target.value)}
                        className="w-full px-2 py-1 text-sm border-0 outline-none focus:bg-blue-50 bg-transparent"
                        style={{ minWidth: '100px' }}
                      >
                        <option value="CAD">CAD</option>
                        <option value="COD">COD</option>
                      </select>
                    </td>
                    <td className="border border-gray-300 px-1 py-0">
                      <input
                        type="text"
                        value={order.CustomerName || ''}
                        onChange={(e) => handleOrderFieldChange(index, 'CustomerName', e.target.value)}
                        className="w-full px-2 py-1 text-sm border-0 outline-none focus:bg-blue-50"
                        style={{ minWidth: '150px' }}
                      />
                    </td>
                    <td className="border border-gray-300 px-1 py-0">
                      <input
                        type="text"
                        value={order.MobileNo || ''}
                        onChange={(e) => handleOrderFieldChange(index, 'MobileNo', e.target.value)}
                        className="w-full px-2 py-1 text-sm border-0 outline-none focus:bg-blue-50"
                        style={{ minWidth: '120px' }}
                      />
                    </td>
                    <td className="border border-gray-300 px-1 py-0">
                      <input
                        type="text"
                        value={order.MobileNo2 || ''}
                        onChange={(e) => handleOrderFieldChange(index, 'MobileNo2', e.target.value)}
                        className="w-full px-2 py-1 text-sm border-0 outline-none focus:bg-blue-50"
                        style={{ minWidth: '120px' }}
                      />
                    </td>
                    <td className={`border border-gray-300 px-1 py-0 ${!order.EmirateId ? 'bg-red-100' : ''}`}>
                      <select
                        value={order.EmirateId || ''}
                        onChange={(e) => handleOrderFieldChange(index, 'EmirateId', e.target.value)}
                        className="w-full px-2 py-1 text-sm border-0 outline-none focus:bg-blue-50 bg-transparent"
                        style={{ minWidth: '120px' }}
                      >
                        <option value="">Select...</option>
                        {emirates.map((emi) => (
                          <option key={emi.Id} value={emi.Id}>{emi.Name}</option>
                        ))}
                      </select>
                    </td>
                    <td className={`border border-gray-300 px-1 py-0 ${!order.LocationId ? 'bg-red-100' : ''}`}>
                      <select
                        value={order.LocationId || ''}
                        onChange={(e) => handleOrderFieldChange(index, 'LocationId', e.target.value)}
                        disabled={!order.EmirateId}
                        className={`w-full px-2 py-1 text-sm border-0 outline-none focus:bg-blue-50 bg-transparent ${!order.EmirateId ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                        style={{ minWidth: '150px' }}
                      >
                        <option value="">Select...</option>
                        {order.Locations?.map((loc) => (
                          <option key={loc.Id} value={loc.Id}>{loc.Name}</option>
                        ))}
                      </select>
                    </td>
                    <td className="border border-gray-300 px-1 py-0">
                      <textarea
                        value={order.Address || ''}
                        onChange={(e) => handleOrderFieldChange(index, 'Address', e.target.value)}
                        className="w-full px-2 py-1 text-sm border-0 outline-none focus:bg-blue-50 resize-none"
                        rows={1}
                        style={{ minWidth: '200px' }}
                      />
                    </td>
                    <td className="border border-gray-300 px-1 py-0">
                      <input
                        type="date"
                        value={order.PickupDate || ''}
                        onChange={(e) => handleOrderFieldChange(index, 'PickupDate', e.target.value)}
                        className="w-full px-2 py-1 text-sm border-0 outline-none focus:bg-blue-50"
                        style={{ minWidth: '100px' }}
                      />
                    </td>
                    <td className="border border-gray-300 px-1 py-0">
                      <input
                        type="date"
                        value={order.DeliveryDate || ''}
                        onChange={(e) => handleOrderFieldChange(index, 'DeliveryDate', e.target.value)}
                        className="w-full px-2 py-1 text-sm border-0 outline-none focus:bg-blue-50"
                        style={{ minWidth: '100px' }}
                      />
                    </td>
                    <td className="border border-gray-300 px-1 py-0">
                      <input
                        type="number"
                        value={order.Quantity || 0}
                        onChange={(e) => handleOrderFieldChange(index, 'Quantity', parseFloat(e.target.value) || 0)}
                        className="w-full px-2 py-1 text-sm border-0 outline-none focus:bg-blue-50 text-right"
                        style={{ minWidth: '80px' }}
                      />
                    </td>
                    <td className="border border-gray-300 px-1 py-0">
                      <input
                        type="number"
                        value={order.ProductPrice || 0}
                        onChange={(e) => handleOrderFieldChange(index, 'ProductPrice', parseFloat(e.target.value) || 0)}
                        className="w-full px-2 py-1 text-sm border-0 outline-none focus:bg-blue-50 text-right"
                        style={{ minWidth: '120px' }}
                      />
                    </td>
                    <td className="border border-gray-300 px-1 py-0">
                      <input
                        type="number"
                        value={order.DeliveryCharge || 0}
                        onChange={(e) => handleOrderFieldChange(index, 'DeliveryCharge', parseFloat(e.target.value) || 0)}
                        className="w-full px-2 py-1 text-sm border-0 outline-none focus:bg-blue-50 text-right"
                        style={{ minWidth: '120px' }}
                      />
                    </td>
                    <td className="border border-gray-300 px-1 py-0 bg-gray-50">
                      <input
                        type="number"
                        value={order.TAX || 0}
                        disabled
                        className="w-full px-2 py-1 text-sm border-0 outline-none bg-gray-50 text-right"
                        style={{ minWidth: '80px' }}
                      />
                    </td>
                    <td className="border border-gray-300 px-1 py-0 bg-gray-50">
                      <input
                        type="number"
                        value={order.CustomerAmount || 0}
                        disabled
                        className="w-full px-2 py-1 text-sm border-0 outline-none bg-gray-50 text-right"
                        style={{ minWidth: '120px' }}
                      />
                    </td>
                    <td className="border border-gray-300 px-1 py-0 bg-gray-50">
                      <input
                        type="number"
                        value={order.ClientAmount || 0}
                        disabled
                        className="w-full px-2 py-1 text-sm border-0 outline-none bg-gray-50 text-right"
                        style={{ minWidth: '120px' }}
                      />
                    </td>
                    <td className="border border-gray-300 px-1 py-0">
                      <input
                        type="text"
                        value={order.Remarks || ''}
                        onChange={(e) => handleOrderFieldChange(index, 'Remarks', e.target.value)}
                        className="w-full px-2 py-1 text-sm border-0 outline-none focus:bg-blue-50"
                        style={{ minWidth: '150px' }}
                      />
                    </td>
                    <td className="border border-gray-300 px-1 py-0 text-center">
                      <input
                        type="checkbox"
                        checked={order.IsPickup || false}
                        onChange={(e) => handleOrderFieldChange(index, 'IsPickup', e.target.checked)}
                        className="w-4 h-4"
                      />
                    </td>
                    <td className="border border-gray-300 px-1 py-0 text-center">
                      <input
                        type="checkbox"
                        checked={order.IsAdvancePay || false}
                        onChange={(e) => handleOrderFieldChange(index, 'IsAdvancePay', e.target.checked)}
                        className="w-4 h-4"
                      />
                    </td>
                    <td className="border border-gray-300 px-1 py-0 text-center">
                      <input
                        type="checkbox"
                        checked={order.IsExchange || false}
                        onChange={(e) => handleOrderFieldChange(index, 'IsExchange', e.target.checked)}
                        className="w-4 h-4"
                      />
                    </td>
                    <td className="border border-gray-300 px-1 py-0 text-center">
                      <input
                        type="checkbox"
                        checked={order.IsFree || false}
                        onChange={(e) => handleOrderFieldChange(index, 'IsFree', e.target.checked)}
                        className="w-4 h-4"
                      />
                    </td>
                    <td className="border border-gray-300 px-1 py-0 text-center">
                      <input
                        type="checkbox"
                        checked={order.IsAdvancePrice || false}
                        onChange={(e) => handleOrderFieldChange(index, 'IsAdvancePrice', e.target.checked)}
                        className="w-4 h-4"
                      />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={25} className="border border-gray-300 px-4 py-12 text-center">
                    <div className="flex flex-col items-center justify-center text-gray-400">
                      <Upload className="w-12 h-12 mb-3 opacity-50" />
                      <p className="text-sm font-medium">No orders loaded</p>
                      <p className="text-xs mt-1">Select a client and upload an Excel file to get started</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Action Buttons */}
        <div className="p-4 border-t border-gray-200 flex items-center justify-center gap-3">
            {loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CircularProgress size={24} sx={{ color: '#46955A' }} />
                <span className="text-sm text-gray-600">Processing...</span>
              </Box>
            ) : (
              <>
                <Button
                  variant="outlined"
                  startIcon={<RefreshCw className="w-4 h-4" />}
                  onClick={handleInit}
                  sx={{
                    borderColor: '#46955A',
                    color: '#46955A',
                    '&:hover': { borderColor: '#3d7d4f', backgroundColor: '#f0f9f4' }
                  }}
                >
                  Refresh
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Save className="w-4 h-4" />}
                  onClick={() => handleUploadOrders(false)}
                  disabled={!selectedClient || orders.length === 0}
                  sx={{
                    backgroundColor: '#46955A',
                    '&:hover': { backgroundColor: '#3d7d4f' },
                    '&:disabled': { backgroundColor: '#9CA3AF' }
                  }}
                >
                  Upload Orders
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Printer className="w-4 h-4" />}
                  onClick={() => handleUploadOrders(true)}
                  disabled={!selectedClient || orders.length === 0}
                  sx={{
                    backgroundColor: '#46955A',
                    '&:hover': { backgroundColor: '#3d7d4f' },
                    '&:disabled': { backgroundColor: '#9CA3AF' }
                  }}
                >
                  Upload & Print
                </Button>
              </>
            )}
          </div>
        </div>
    </div>
  );
};

export default OrderUpload;

